import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Scissors } from "lucide-react";
import { toast } from "sonner";

const ASPECTS = [
  { key: "facial_hair", verdictKey: "clean_shaven", label: "Facial hair within policy" },
  { key: "shoes", verdictKey: "wearing_shoes", label: "Proper shoes" },
  { key: "uniform", verdictKey: "proper_uniform", label: "Proper uniform" },
];

export const GroomingFeedbackDialog = ({ open, onOpenChange, check, onSubmitted }) => {
  const gc = check?.grooming_check || {};
  const [vals, setVals] = useState({ facial_hair: true, shoes: true, uniform: true });
  const [grantException, setGrantException] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setVals({
        facial_hair: !!gc.clean_shaven,
        shoes: !!gc.wearing_shoes,
        uniform: !!gc.proper_uniform,
      });
      setGrantException(false);
      setNote("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/grooming-checks/${check.id}/feedback`, {
        correct_facial_hair: grantException ? true : vals.facial_hair,
        correct_shoes: vals.shoes,
        correct_uniform: vals.uniform,
        grant_facial_hair_exception: grantException,
        note: note.trim() || null,
      });
      toast.success("Feedback recorded — the system will learn from this.");
      onOpenChange(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save feedback");
    } finally {
      setSaving(false);
    }
  };

  if (!check) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="grooming-feedback-dialog">
        <DialogHeader>
          <DialogTitle>Correct the AI · {check.user_name}</DialogTitle>
          <DialogDescription>
            Set what the verdict should actually be. Your correction is stored so the AI stops repeating the same mistake.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="text-[11px] uppercase tracking-widest text-zinc-400">Correct verdict (tick = compliant)</div>
          {ASPECTS.map((a) => {
            const aiSaid = !!gc[a.verdictKey];
            const disabled = a.key === "facial_hair" && grantException;
            return (
              <label
                key={a.key}
                className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-3 py-2 cursor-pointer"
                data-testid={`grooming-correct-${a.key}`}
              >
                <span className="flex items-center gap-2">
                  <Checkbox
                    checked={disabled ? true : vals[a.key]}
                    disabled={disabled}
                    onCheckedChange={(c) => setVals((v) => ({ ...v, [a.key]: !!c }))}
                  />
                  <span className="text-sm font-medium">{a.label}</span>
                </span>
                <span className={`text-[10px] uppercase tracking-widest ${aiSaid ? "text-emerald-600" : "text-red-600"}`}>
                  AI: {aiSaid ? "OK" : "Fail"}
                </span>
              </label>
            );
          })}

          <label
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 cursor-pointer"
            data-testid="grooming-grant-exception"
          >
            <Checkbox checked={grantException} onCheckedChange={(c) => setGrantException(!!c)} className="mt-0.5" />
            <span>
              <span className="text-sm font-semibold flex items-center gap-1"><Scissors className="w-3.5 h-3.5" /> Allow a groomed beard for {check.user_name}</span>
              <span className="block text-xs text-amber-800/80 mt-0.5">
                Grants a standing exception — future checks will never flag this employee for facial hair.
              </span>
            </span>
          </label>

          <div>
            <Label htmlFor="gf-note" className="text-xs text-zinc-500">Note (helps the AI learn — what was wrong & why)</Label>
            <Textarea
              id="gf-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Beard is on cheeks/jaw — should have been flagged."
              className="mt-1 text-sm"
              rows={2}
              data-testid="grooming-feedback-note"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-zinc-950" data-testid="grooming-feedback-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
